import get from 'lodash/get';
import type { FlagConfiguration, FlagMessage } from '@atlassian/jira-flags';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import {
	REQUEST_GROUP_DUPLICATE_NAME_ERROR,
	REQUEST_GROUP_NAME_REQUIRED_ERROR,
	REQUEST_GROUP_NAME_TOO_LONG_ERROR,
	REQUEST_GROUP_NOT_FOUND_ERROR,
	REQUEST_TYPE_INVALID_ERROR,
	REQUEST_TYPE_NOT_FOUND_ERROR,
} from '../../constants';
import {
	DELETE_GROUP,
	REORDER_GROUP,
	UPDATE_GROUP,
	ADD_FORM,
	REORDER_FORM,
	REMOVE_FORM,
} from './constants';
import messages from './messages';
import type { ActionType } from './types';

export const defaultErrorConfig: FlagConfiguration = {
	type: 'error',
	isAutoDismiss: true,
	title: messages.genericErrorTitle,
	description: messages.genericErrorDescription,
};

export const actionConfig: Partial<FlagConfiguration> = {
	actions: [
		{
			content: messages.refreshAction,
			onClick: () => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.reload();
			},
		},
	],
};

const makeErrorConfig = (
	title: FlagMessage,
	descriptionOverride: FlagMessage | null | undefined,
	error: ErrorResponse,
	hasActions = true,
) => ({
	...defaultErrorConfig,
	...(hasActions ? actionConfig : undefined),
	title,
	description:
		descriptionOverride ||
		(get(error, 'response.stateReverted', false) ? messages.dataReverted : messages.dataStale),
});

const ERROR_KEY_MESSAGE: {
	[key: string]: MessageDescriptor;
} = {
	[REQUEST_GROUP_NOT_FOUND_ERROR]: messages.groupNotFound,
	[REQUEST_GROUP_DUPLICATE_NAME_ERROR]: messages.updateGroupNameUsed,
	[REQUEST_TYPE_INVALID_ERROR]: messages.formInvalid,
	[REQUEST_TYPE_NOT_FOUND_ERROR]: messages.formNotFound,
	[REQUEST_GROUP_NAME_REQUIRED_ERROR]: messages.missingGroupNameError,
	[REQUEST_GROUP_NAME_TOO_LONG_ERROR]: messages.groupNameTooLongError,
};

const getErrorFlag = (action: ActionType, error: ErrorResponse): FlagConfiguration => {
	const reasonKey = error.response && error.response.reasonKey;
	switch (action) {
		case DELETE_GROUP: {
			return makeErrorConfig(messages.deleteGroupTitle, ERROR_KEY_MESSAGE[reasonKey], error);
		}
		case REORDER_GROUP: {
			return makeErrorConfig(messages.reorderGroupTitle, undefined, error);
		}
		case UPDATE_GROUP: {
			const isNameUsedAlready = reasonKey === REQUEST_GROUP_DUPLICATE_NAME_ERROR;

			return makeErrorConfig(
				messages.updateGroupTitle,
				ERROR_KEY_MESSAGE[reasonKey],
				error,
				!isNameUsedAlready,
			);
		}
		case ADD_FORM: {
			return makeErrorConfig(messages.addFormTitle, ERROR_KEY_MESSAGE[reasonKey], error);
		}
		case REMOVE_FORM: {
			return makeErrorConfig(messages.removeFormTitle, undefined, error);
		}
		case REORDER_FORM: {
			return makeErrorConfig(messages.reorderFormTitle, ERROR_KEY_MESSAGE[reasonKey], error);
		}
		default:
			return { ...defaultErrorConfig, ...actionConfig };
	}
};

export default getErrorFlag;
